import Keycloak from 'keycloak-js'

import getEnvFromLocation from 'utils/getEnvFromLocation'

import { CLIENT_IDS, ENDPOINT, REALMS } from './keycloak.consts'
import type { TKeycloakEnv } from './keycloak.types'

// Reuse the app-wide env detector (already used by datadog, feature-flags, and the
// Apollo authLink's `realm` header) instead of duplicating winnin-auth's copy. This
// also aligns keycloak login with the realm the API already receives per request.
const env = getEnvFromLocation() as TKeycloakEnv

export const keycloak = new Keycloak({
  url: ENDPOINT[env],
  realm: REALMS[env],
  clientId: CLIENT_IDS[env],
})

// Registered at module load, before any init() can run, so multi-tab sessions still
// force a reload as they do today. Ported from KeycloakWrapper/index.tsx:114-119,
// where it was (incorrectly) attached inside a useEffect after init.
keycloak.onAuthRefreshSuccess = () => {
  const isMultisession = keycloak.tokenParsed?.multisession
  if (isMultisession) {
    location.reload()
  }
}

// Cross-tab logout: keycloak's checkLoginIframe (on by default) detects when the SSO session ends
// in another tab and fires onAuthLogout. `@react-keycloak/web` used to handle this by flipping its
// auth state (re-render -> redirect to login); our in-house provider owns it now. Reload so check-sso
// re-runs, sees no session, and AuthGate/PrivateRoute send this tab to login — without it the tab
// keeps stale content until the next request fails. Registered before init(), like the callback above.
keycloak.onAuthLogout = () => {
  location.reload()
}

// Proactive token refresh: keycloak fires onTokenExpired when the access token lapses. Without a
// handler an idle tab (no GraphQL requests) lets the token — and eventually the refresh token /
// SSO session — expire silently. `@react-keycloak/web` refreshed here with updateToken(5); we keep
// that so an open tab stays signed in. On failure (refresh token gone) we swallow: the next request
// or the checkLoginIframe logout path handles the dead session. Registered before init(), like above.
keycloak.onTokenExpired = () => {
  keycloak.updateToken(5).catch(() => {})
}
